import { Avatar, CircularProgress } from "@mui/material";
import Plus from "assets/img/svg/plus";
import type { TArticleData } from "containers/ArticleFormContainer";
import { TArticleFormData } from "containers/ArticleFormContainer";
import type { IBeneficiar } from "containers/Beneficiars/types";
import { IRepresentative } from "containers/Beneficiars/types";
import type { ITermFormValues } from "containers/TermFormContainer";
import type { ITransferTermFormValues } from "containers/TransferTermFormContainer";
import { formatLocaleDateString } from "helpers/utils";
import useAPIPagination from "hooks/useAPIPagination";
import { useMemo, type ChangeEvent, type HTMLAttributes } from "react";
import type { Control, FieldError, FieldErrorsImpl, Merge, UseFormWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import type { ITransferee } from "store/transfereesStore";

import variables from "../../../assets/styles/_variables.module.scss";
import AutocompleteFormComponent from "../FormComponent/AutocompleteFormComponent";
import StyledTextField from "../StyledTextField/StyledTextField";
import UserAvatar from "../UserAvatar";

interface IPeopleAutocompleteField {
    /**
     * Data used for options.
     */
    data: IBeneficiar[] | ITransferee[];
    /**
     * Type of data (mostly needed for useAPIPagination)
     */
    type: "beneficiaries" | "transferees";
    label: string;
    control: Control<ITransferTermFormValues> | Control<ITermFormValues> | Control<TArticleData>;
    /**
     * Errors specific to field so errors.beneficiary for instance.
     */
    errors: Merge<FieldError, FieldErrorsImpl> | undefined;
    controlName: string;
    watch: UseFormWatch<ITransferTermFormValues> | UseFormWatch<ITermFormValues> | UseFormWatch<TArticleData>;
    required?: boolean;
    disabled?: boolean;
    customRequiredMessage?: string;
    /**
     * String to display on the add button. If empty, no add button displayed.
     */
    addButton?: string;
    /**
     * What kind of item is linked to them (titles or articles)
     */
    titleType?: "article" | "title";
}

function isListedPerson(value: unknown): value is IBeneficiar | ITransferee {
    if (typeof value !== "object" || value == null || !("id" in value)) return false;
    const id = Number(value.id);
    return Number.isFinite(id) && id !== -1;
}

/**
 * Autocomplete form component specific to displaying "people" aka beneficiaries or transferees.
 */
const PeopleAutocompleteField = ({
    data,
    type,
    label,
    control,
    controlName,
    errors,
    watch,
    required,
    disabled,
    customRequiredMessage,
    addButton,
    titleType = "title",
}: IPeopleAutocompleteField) => {
    const [t] = useTranslation();

    const { handleQuery, setOffsetPagination, requestStatus } = useAPIPagination({
        type: type,
        defaultLimitPagination: 10,
        autocomplete: true,
    });

    const dataLoading = requestStatus === "pending" || requestStatus === "idle";
    const selectedPerson = (watch as (name: string) => unknown)(controlName);
    const options = useMemo(() => {
        if (!isListedPerson(selectedPerson)) return data;
        const isAlreadyListed = data.some((item) => item.id == selectedPerson.id);
        return isAlreadyListed ? data : [selectedPerson, ...data];
    }, [data, selectedPerson]);

    const handleOnScrollEnd = () => {
        setOffsetPagination(data?.length ?? 0);
    };

    const renderInput = (params) => (
        <StyledTextField
            {...params}
            error={!!errors}
            helperText={errors?.message ?? ""}
            onChange={(e: ChangeEvent<HTMLInputElement>) => handleQuery(e)}
            InputProps={{
                ...params.InputProps,
                endAdornment: (
                    <>
                        {dataLoading ? (
                            <CircularProgress sx={{ color: variables["primary-purple"] }} size={18} />
                        ) : null}
                        {params.InputProps.endAdornment}
                    </>
                ),
            }}
        />
    );

    const renderOptionText = (option: any) => {
        const surname = (option?.surname ?? "").toUpperCase();
        const name = type === "beneficiaries" ? option?.name ?? "" : (option?.name ?? "").toUpperCase();
        return `${surname} ${name}`.trim() || "";
    };

    const renderOption = (props: HTMLAttributes<HTMLLIElement>, option: any) => {
        const countType =
            type === "beneficiaries" ? (titleType === "title" ? "titlesCount" : "articlesCount") : "transferTermCount";
        const beneficiariesCountLabel =
            titleType === "title" ? t("ConnectTitle.Titles") : t("Beneficiars.ArticlesCount");
        const itemCount = option[countType] ?? 0;
        const surname = (option?.surname ?? "").toUpperCase();
        const name = type === "beneficiaries" ? option?.name ?? "" : (option?.name ?? "").toUpperCase();

        return option.addElement ? (
            <li {...props} key={option.id} className="autocomplete-new-element">
                <div className="content">
                    <Plus />
                    {option.addElement}
                </div>
            </li>
        ) : (
            <li
                {...props}
                key={option.id}
                className={`autocomplete-beneficiaries${
                    option.id == watch(`${controlName}.id` as any) ? " selected" : ""
                }`}
            >
                <div className="autocomplete-beneficiaries-left-content">
                    <UserAvatar name={name} surname={surname} avatarUrl={option.avatarUrl} size="x-small" />
                    <span>{`${surname} ${name}`}</span>
                </div>
                <div className="autocomplete-beneficiaries-right-content">
                    <span style={{ fontSize: "12px" }}>
                        {itemCount}{" "}
                        {type === "beneficiaries" ? beneficiariesCountLabel : t("Transferees.Terms").toLowerCase()}
                    </span>
                    {option?.creationDate ? (
                        <span style={{ fontSize: "12px" }}>
                            {t("NewArticle.AddedThe")}{" "}
                            {formatLocaleDateString({
                                date: option.creationDate,
                                options: { numeric: true, timeZone: "UTC" },
                            })}
                        </span>
                    ) : (
                        ""
                    )}
                </div>
            </li>
        );
    };

    return (
        <AutocompleteFormComponent
            label={label}
            options={options}
            renderInput={renderInput}
            renderOptionText={renderOptionText}
            renderOption={renderOption}
            control={control}
            controlName={controlName}
            error={errors}
            required={required}
            customRequiredMessage={customRequiredMessage}
            disabled={disabled}
            addElement={addButton}
            onScrollEnd={() => handleOnScrollEnd()}
            onScrollEndLoading={requestStatus}
            loading={dataLoading}
        />
    );
};

export default PeopleAutocompleteField;
